/**
 * What the app and the Worker say to each other: the shapes of the API's answers, and the rules for
 * names, addresses and passwords both sides check.
 */

import type { Counts } from './layout';
import type { PreviewShape } from './preview';

export type Role = 'admin' | 'lead';

export interface Me {
  email: string;
  name: string;
  role: Role;
}

export interface Who {
  email: string;
  name: string;
}

export interface PersonRow extends Who {
  role: Role;
  /** active: has a password; invited: hasn't used their set-up link yet; removed: can't sign in. */
  status: 'active' | 'invited' | 'removed';
  addedBy: Who;
  addedAt: number;
  seenAt: number | null;
}

export interface EventRow {
  id: string;
  hall: string;
  name: string;
  startsOn: string | null;
  endsOn: string | null;
  layouts: number;
  madeBy: Who;
  changedAt: number;
  deletedAt: number | null;
}

export interface LayoutRow {
  id: string;
  eventId: string;
  hall: string;
  name: string;
  version: number;
  counts: Counts;
  preview: PreviewShape[];
  showReciters: boolean;
  madeBy: Who;
  madeAt: number;
  savedBy: Who;
  savedAt: number;
  deletedAt: number | null;
  deletedBy: Who | null;
}

export interface VersionRow {
  version: number;
  summary: string;
  savedBy: Who;
  savedAt: number;
  restoredFrom: number | null;
}

export interface LogRow {
  at: number;
  who: Who;
  action: string;
  version: number | null;
  detail: string;
}

export interface LinkRow {
  token: string;
  madeBy: Who;
  madeAt: number;
  usedAt: number | null;
  revokedAt: number | null;
}

/** A save refused because someone saved since: who, when, and the version that is current now. */
export interface StaleSave {
  error: string;
  current: { version: number; savedBy: Who; savedAt: number };
}

export const NAME_MAX = 120;
export const PASSWORD_MIN = 10;
export const PASSWORD_MAX = 200;

/** A display name: trimmed, inner spaces collapsed, 1 to 120 characters; null if there is none. */
export function cleanName(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const s = raw.normalize('NFC').replace(/\s+/g, ' ').replace(/[\u0000-\u001f\u007f]/g, '').trim();
  return s.length >= 1 && s.length <= NAME_MAX ? s : null;
}

/** An email address, lower-cased, if it looks like one. */
export function cleanEmail(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const s = raw.trim().toLowerCase();
  return s.length <= 254 && /^[^@\s,;<>"]+@[^@\s,;<>"]+\.[^@\s,;<>".]{2,}$/.test(s) ? s : null;
}

/** An event date, YYYY-MM-DD, or null. */
export function cleanDay(raw: unknown): string | null {
  if (typeof raw !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const [y, m, d] = raw.split('-').map(Number) as [number, number, number];
  const day = new Date(Date.UTC(y, m - 1, d));
  return day.getUTCFullYear() === y && day.getUTCMonth() === m - 1 && day.getUTCDate() === d ? raw : null;
}

const COMMON = new Set([
  'password', 'password1', 'password12', 'password123', 'passw0rd', '1234567890', '0123456789', '12345678910', 'qwertyuiop',
  'asdfghjkl;', '1q2w3e4r5t', 'iloveyou12', 'letmein123', 'welcome123', 'abcdefghij', 'mawkeb1234', 'mawkeb12345',
  'mawkeb2026', 'mawkebsaheb', 'sahebalzaman', 'zahraahall', 'tahafoundation', 'venue12345',
]);

/** Why a password won't do, in plain words, or null when it's fine. */
export function passwordProblem(password: string, email: string, name: string): string | null {
  if (password.length < PASSWORD_MIN) return `Use at least ${PASSWORD_MIN} characters.`;
  if (password.length > PASSWORD_MAX) return `Use at most ${PASSWORD_MAX} characters.`;
  const plain = password.toLowerCase();
  if (new Set(plain).size < 4) return 'Use a password with more different characters.';
  if (COMMON.has(plain.replace(/\s+/g, ''))) return 'That password is too common. Try a short phrase only you would think of.';
  const local = (email.split('@')[0] ?? '').toLowerCase();
  const named = name.toLowerCase();
  // any part of the name or address four letters or longer: "zainab", "joiner", "zainabjoiner", "z.joiner"
  const parts = [local, ...local.split(/[._+-]+/), named.replace(/\s+/g, ''), ...named.split(/\s+/)].filter((w) => w.length >= 4);
  if (parts.some((w) => plain.includes(w))) return 'Don’t use your name or email address in your password.';
  return null;
}

/** A placement that is a reciter's avatar (milestone 4 uploads them as "reciter:<id>"); hidden from view-only links unless allowed. */
export function isReciter(item: string): boolean {
  return item.startsWith('reciter:');
}
